import React from "react";
import {
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { WeiboIcon } from "react-share";

const BASE_URL = process.env.REACT_APP_SERVER_URL;

const tableHeaders = [
  { id: 1,  label: "Title" },
  { id: 2,  label: "Coupon Code" },
  { id: 3,  label: "Description" },
  { id: 4,  label: "Discount" },
  { id: 5,  label: "Start Date" },
  { id: 6,  label: "Expiry Date" },
  { id: 7,  label: "Coupon Type" },
  { id: 8,  label: "Broker Code" },
  { id: 9,  label: "Max Redemptions" },
  { id: 10, label: "Status" },
  { id: 11, label: "Deeplink" },
  { id: 12, label: "Image" },
  { id: 13, label: "Actions" },
];

const headCellSx = {
  fontWeight: 700,
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  whiteSpace: "nowrap",
  py: 1,
  px: 1,
  color: "#fff",
  background: "#04343a",
  borderBottom: "none",
};

const bodyCellSx = {
  py: 0.5,
  px: 1,
  fontSize: "0.65rem",
  color: "#333",
  whiteSpace: "nowrap",
};

const UserCouponsTable = ({
  userCouponsData,
  handleEditCoupon,
  handleDeleteCoupon,
}) => {
  return (
    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0 1px 6px rgba(0,0,0,0.08)",  }}>
      <Table size="small" aria-label="coupons table">
        {/* HEAD */}
        <TableHead>
          <TableRow>
            {tableHeaders.map((item) => (
              <TableCell key={item.id} sx={headCellSx}>
                {item.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {/* BODY */}
        <TableBody>
          {userCouponsData?.map((row, index) => (
            <TableRow
              key={index}
              sx={{
                "&:hover": { backgroundColor: "#f0faf5" },
                "&:last-child td": { border: 0 },
              }}
            >
              {/* Title */}
              <TableCell sx={{ ...bodyCellSx, fontWeight: 600, maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis" }}>
                {row.title}
              </TableCell>

              {/* Coupon Code */}
              <TableCell sx={bodyCellSx}>
                <Chip
                  label={row.coupon_code}
                  size="small"
                  sx={{ fontWeight: 700, fontSize: "0.75rem", bgcolor: "#e6f4f1", color: "#016551", letterSpacing: "0.5px" }}
                />
              </TableCell>

              {/* Description */}
              <TableCell sx={{ ...bodyCellSx, maxWidth: 100 }}>
                <Tooltip title={row.description} placement="top" arrow>
                  <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 90 }}>
                    {row.description}
                  </span>
                </Tooltip>
              </TableCell>

              {/* Discount */}
              <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: "#016551" }}>
                {row.discount}
              </TableCell>

              {/* Start Date */}
              <TableCell sx={bodyCellSx}>
                {new Date(row.start_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
              </TableCell>

              {/* Expiry Date */}
              <TableCell sx={bodyCellSx}>
                {new Date(row.expiry_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
              </TableCell>

              {/* Coupon Type */}
              <TableCell sx={bodyCellSx}>{row.coupon_type}</TableCell>

              {/* Broker Code */}
              <TableCell sx={bodyCellSx}>{row.broker_code || "—"}</TableCell>

              {/* Max Redemptions */}
              <TableCell sx={{ ...bodyCellSx, textAlign: "center" }}>{row.max_redemption}</TableCell>

              {/* Status */}
              <TableCell sx={bodyCellSx}>
                <Chip
                  label={row.active ? "Active" : "Inactive"}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    bgcolor: row.active ? "#dcfce7" : "#fee2e2",
                    color: row.active ? "#15803d" : "#b91c1c",
                  }}
                />
              </TableCell>

              {/* Deeplink */}
              <TableCell sx={{ ...bodyCellSx, maxWidth: 90 }}>
                {row.deeplink_url ? (
                  <Tooltip title={row.deeplink_url} placement="top" arrow>
                    <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 80, color: "#016551", cursor: "default" }}>
                      {row.deeplink_url}
                    </span>
                  </Tooltip>
                ) : "—"}
              </TableCell>

              {/* Image Preview */}
              <TableCell sx={{ ...bodyCellSx, textAlign: "center" }}>
                {row.banner_image ? (
                  <Tooltip
                    title={
                      <img
                        src={`${BASE_URL}${row.banner_image}`}
                        alt="Banner Preview"
                        style={{ width: 250, borderRadius: 8 }}
                      />
                    }
                    placement="top"
                  >
                    <IconButton size="small">
                      <VisibilityIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Tooltip>
                ) : "—"}
              </TableCell>

              {/* Actions */}
              <TableCell sx={bodyCellSx}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title="Edit" arrow>
                    <IconButton size="small" onClick={() => handleEditCoupon(row)} sx={{ color: "#04343a", "&:hover": { bgcolor: "#e6f4f1" } }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <IconButton size="small" onClick={() => handleDeleteCoupon(row)} sx={{ color: "#dc2626", "&:hover": { bgcolor: "#fef2f2" } }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {userCouponsData?.length === 0 && (
        <Typography variant="body1" sx={{ p: 4, textAlign: "center", color: "#94a3b8" }}>
          No coupons found
        </Typography>
      )}
    </TableContainer>
  );
};

export default UserCouponsTable;
